import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useState, useEffect } from "react";

type Filters = {
  search: string;
  level: string;
  price: string;
  sort: string;
  featured: boolean;
};

type CourseFiltersProps = {
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
};

export const CourseFilters = ({ filters, onFiltersChange }: CourseFiltersProps) => {
  const { t } = useLanguage();
  const [searchInput, setSearchInput] = useState(filters.search);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      onFiltersChange({ ...filters, search: searchInput });
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleLevelChange = (level: string) => {
    onFiltersChange({ ...filters, level });
  };

  const handlePriceChange = (price: string) => {
    onFiltersChange({ ...filters, price });
  };

  const handleSortChange = (sort: string) => {
    onFiltersChange({ ...filters, sort });
  };

  const toggleFeatured = () => {
    onFiltersChange({ ...filters, featured: !filters.featured });
  };

  const levelOptions = [
    { value: "all", label: t("courses.allLevels") },
    { value: "beginner", label: t("courses.beginner") },
    { value: "intermediate", label: t("courses.intermediate") },
    { value: "advanced", label: t("courses.advanced") },
  ];

  const priceOptions = [
    { value: "all", label: t("courses.allPrices") },
    { value: "free", label: t("courses.free") },
    { value: "paid", label: t("courses.paid") },
  ];

  const sortOptions = [
    { value: "newest", label: t("courses.sortNewest") },
    { value: "popular", label: t("courses.sortPopular") },
    { value: "price-low", label: t("courses.sortPriceLow") },
    { value: "price-high", label: t("courses.sortPriceHigh") },
  ];

  return (
    <div className="space-y-6">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
        <Input
          type="text"
          placeholder={t("courses.search")}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="pl-10 bg-card/50 border-border focus:border-holographic-cyan"
        />
      </div>

      {/* Filter Pills and Sort */}
      <div className="flex flex-wrap gap-4 items-center">
        {/* Level Pills */}
        <div className="flex gap-2">
          {levelOptions.map((option) => (
            <Button
              key={option.value}
              variant={filters.level === option.value ? "default" : "outline"}
              size="sm"
              onClick={() => handleLevelChange(option.value)}
              className={
                filters.level === option.value
                  ? "bg-holographic-cyan text-background hover:bg-holographic-cyan/90"
                  : "hover:border-holographic-cyan/50"
              }
            >
              {option.label}
            </Button>
          ))}
        </div>

        {/* Price Pills */}
        <div className="flex gap-2">
          {priceOptions.map((option) => (
            <Button
              key={option.value}
              variant={filters.price === option.value ? "default" : "outline"}
              size="sm"
              onClick={() => handlePriceChange(option.value)}
              className={
                filters.price === option.value
                  ? "bg-holographic-pink text-background hover:bg-holographic-pink/90"
                  : "hover:border-holographic-pink/50"
              }
            >
              {option.label}
            </Button>
          ))}
        </div>

        {/* Featured Toggle */}
        <Button
          variant={filters.featured ? "default" : "outline"}
          size="sm"
          onClick={toggleFeatured}
          className={
            filters.featured
              ? "bg-holographic-green text-background hover:bg-holographic-green/90"
              : "hover:border-holographic-green/50"
          }
        >
          {t("courses.featured")}
        </Button>

        {/* Sort Dropdown */}
        <div className="ml-auto">
          <Select value={filters.sort} onValueChange={handleSortChange}>
            <SelectTrigger className="w-[200px] bg-card/50 border-border">
              <SelectValue placeholder={t("courses.sortBy")} />
            </SelectTrigger>
            <SelectContent>
              {sortOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};
